import { FC, useMemo } from "react"
import { DataGrid, PreviousLocation } from "@/core/components"
import {
  getGridStringOperators,
  GridActionsCellItem,
  GridCallbackDetails,
  gridClasses,
  GridColDef,
  GridFilterModel,
} from "@mui/x-data-grid-pro"
import { GRID_PAGE_SIZE_OPTIONS } from "@/core/constants"
import { useAppDispatch, useAppSelector } from "@/modules/app/hooks"
import { HideSource, Preview, SettingsBackupRestore } from "@mui/icons-material"
import { useNavigate } from "@/core/hooks"
import {
  getTgstatBotState,
  setMyChatsFilter,
  setMyChatsPaginationModel,
  setShowHidden,
  useGetActiveBotQuery,
  useGetMyChatsQuery,
  useHideChatMutation,
} from "../store"
import { TgstatBot } from "../interfaces"
import { formatDateAndTime } from "@/core/utils"
import { CircularProgress, FormControlLabel, Switch } from "@mui/material"

interface IProps {}
export const MyChatsTable: FC<IProps> = (props) => {
  const navigate = useNavigate<PreviousLocation>()
  const dispatch = useAppDispatch()
  const {
    myChats: { page, pageSize },
    showHidden,
    myChatsFilters,
  } = useAppSelector(getTgstatBotState)
  const { isFetching, data: dataAndTotal } = useGetMyChatsQuery({
    queryParams: {
      page: page + 1,
      page_size: pageSize,
      with_hidden: showHidden,
      filters: myChatsFilters,
    },
  })
  const [data, totalCount] = dataAndTotal ?? []
  const { data: activeBot } = useGetActiveBotQuery({})
  const [hide, { isLoading: isHideChatLoading }] = useHideChatMutation()

  const columns: GridColDef<TgstatBot.Chat>[] = useMemo(
    () => [
      {
        headerName: "Бот ID",
        field: "bot_id",
        flex: 1,
        filterable: false,
      },
      {
        headerName: "Название",
        field: "chat_title",
        flex: 2,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
          (o) => o?.value === "contains",
        ),
      },
      {
        headerName: "Юзернейм",
        field: "chat_username",
        flex: 2,
        filterable: false,
      },
      {
        headerName: "Статус",
        field: "status",
        flex: 1,
        filterable: false,
      },
      {
        headerName: "Может создавать ссылки",
        field: "can_invite_users",
        flex: 1,
        type: "boolean",
        filterable: false,
      },
      {
        headerName: "Дата добавления",
        field: "created_at",
        flex: 1,
        filterable: false,
        valueFormatter: ({ value }) => formatDateAndTime(value),
      },
    ],
    [],
  )

  const handleFilterModeChange = (
    model: GridFilterModel,
    details: GridCallbackDetails<"filter">,
  ) => {
    dispatch(
      setMyChatsPaginationModel({
        page: 0,
        pageSize,
      }),
    )
    const filter = model.items.reduce<{ [key: string]: string }>(
      (filter, item) => {
        filter[item.field] = item.value
        return filter
      },
      {},
    )

    dispatch(setMyChatsFilter(filter))
  }

  return (
    <>
      <FormControlLabel
        control={
          <Switch
            // value={showHidden}
            checked={showHidden}
            onChange={(e, value) => dispatch(setShowHidden(value))}
            inputProps={{ "aria-label": "Показать скрытые" }}
          />
        }
        label="Показать скрытые"
        labelPlacement="start"
      />
      <DataGrid<TgstatBot.Chat>
        columns={columns}
        rows={data ?? []}
        getRowId={(row) => `${row.chat_id}_${row.bot_id}`}
        rowCount={totalCount ?? 0}
        loading={isFetching}
        pagination
        paginationMode="server"
        pageSizeOptions={GRID_PAGE_SIZE_OPTIONS}
        onPaginationModelChange={(model) =>
          dispatch(setMyChatsPaginationModel(model))
        }
        initialState={{
          pagination: {
            paginationModel: { page, pageSize },
          },
          columns: {
            columnVisibilityModel: {
              username: false,
            },
          },
        }}
        getRowClassName={(params) =>
          params.row.is_hidden
            ? `${gridClasses.row}--hidden`
            : params.row.bot_id !== activeBot?.id
              ? `${gridClasses.row}--disabled`
              : ""
        }
        filterMode="server"
        unstable_headerFilters
        filterDebounceMs={700}
        onFilterModelChange={handleFilterModeChange}
        filterModel={{
          items: myChatsFilters
            ? Object.entries(myChatsFilters).map(([field, value]) => ({
                field,
                value,
                operator: "contains",
              }))
            : [],
        }}
        rowActions={[
          (params) => (
            <GridActionsCellItem
              label="Открыть"
              icon={<Preview />}
              onClick={() =>
                navigate(
                  { pathname: `${params.row.bot_id}/${params.row.chat_id}` },
                  {
                    state: {
                      from: window.location.pathname,
                      title: "К списку чатов",
                    },
                  },
                )
              }
            />
          ),
          (params) => (
            <GridActionsCellItem
              label={params.row.is_hidden ? "Отобразить" : "Скрыть"}
              icon={
                isHideChatLoading ? (
                  <CircularProgress size="small" />
                ) : params.row.is_hidden ? (
                  <SettingsBackupRestore />
                ) : (
                  <HideSource />
                )
              }
              showInMenu
              onClick={() =>
                hide({
                  pathParams: {
                    bot_id: params.row.bot_id,
                    chat_id: params.row.chat_id,
                  },
                  body: {
                    is_hidden: !params.row.is_hidden,
                  },
                })
              }
              disabled={isHideChatLoading}
            />
          ),
        ]}
      />
    </>
  )
}
